import { apiClient } from './client';
import { extractBlobErrorMessage } from './payments';
import { downloadBlob } from '../utils/downloadBlob';

export const MANAGEMENT_REPORT_KEYS = [
  'executive',
  'target-revenue',
  'programme-performance',
  'intake-performance',
  'targets',
  'revenue',
  'fis',
  'lead-conversion',
  'dropouts',
  'counsellor-performance',
  'university-performance',
  'data-quality',
] as const;
export type ManagementReportKey = (typeof MANAGEMENT_REPORT_KEYS)[number];

export type Period = '1m' | '2m' | '3m' | '6m' | '1y' | 'custom' | 'all';
export type StudentStage = 'ACTIVE' | 'DROPPED_OUT' | 'IN_BATCH' | 'AWAITING_DEPARTURE' | 'DEPARTED';

export interface ManagementFilters {
  period: Period;
  from?: string;
  to?: string;
  branchId?: number;
  countryId?: number;
  universityId?: number;
  courseId?: number;
  intakeId?: number;
  counselorId?: number;
  leadStatusId?: number;
  stage?: StudentStage;
  dropoutFrom?: string;
  dropoutTo?: string;
}

export type ValueFormat = 'number' | 'percent' | 'currency';

export interface Kpi {
  key: string;
  label: string;
  value: number | null;
  format: ValueFormat;
  hint?: string;
  report?: ManagementReportKey;
  /** unavailable: the CRM has no data for it; incomplete: some records couldn't be counted. */
  status?: 'unavailable' | 'incomplete';
  note?: string;
  /** Same figure for the previous period of equal length. */
  previous?: number | null;
}
export interface ReportColumn {
  key: string;
  label: string;
  format?: ValueFormat | 'date';
}
/** `rowType` marks subtotal / total lines; `drill_*` keys are the filters a line drills into. */
export type ReportRow = Record<string, string | number | null>;
export interface ReportTable {
  key: string;
  title: string;
  columns: ReportColumn[];
  rows: ReportRow[];
  hierarchical?: boolean;
  description?: string;
}
export interface ChartData {
  labels: string[];
  series: Array<{ label: string; data: number[] }>;
  format?: ValueFormat;
}
export interface ManagementReport {
  key: ManagementReportKey;
  title: string;
  subtitle: string;
  periodLabel: string;
  periodBasis: string;
  previousPeriodLabel?: string;
  filters: string[];
  kpis: Kpi[];
  charts: Record<string, ChartData>;
  tables: ReportTable[];
  /** Target Revenue: the KPI workbook's sheet, cell by cell. */
  sheet?: Sheet;
  notes: string[];
}

export interface SheetCell {
  address: string;
  text: string;
  value: number | string | null;
  kind: 'label' | 'data' | 'formula' | 'empty';
  formula?: string;
  bold?: boolean;
  size?: number;
  color?: string;
  fill?: string;
  align?: string;
  rowSpan?: number;
  colSpan?: number;
}
export interface Sheet {
  columns: Array<{ letter: string; width: number }>;
  rows: Array<{ height?: number; cells: Array<SheetCell | null> }>;
}

export interface ManagementOptions {
  canChooseBranch: boolean;
  branches: Array<{ id: number; name: string }>;
  counsellors: Array<{ id: number; name: string; isActive: boolean }>;
  countries: Array<{ id: number; name: string }>;
  universities: Array<{ id: number; name: string; isActive: boolean; countryId: number | null }>;
  courses: Array<{ id: number; name: string; universityId: number; isActive: boolean }>;
  intakes: Array<{ id: number; name: string; isActive: boolean; month: number; year: number }>;
  leadStatuses: Array<{ id: number; name: string }>;
}

const params = (filters: ManagementFilters) =>
  Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== '' && v !== null));

export const getManagementOptions = async (): Promise<ManagementOptions> => {
  const { data } = await apiClient.get('/management-reports/options');
  return data.data;
};

export const getManagementReport = async (key: ManagementReportKey, filters: ManagementFilters): Promise<ManagementReport> => {
  const { data } = await apiClient.get(`/management-reports/${key}`, { params: params(filters), timeout: 180_000 });
  return data.data;
};

const fetchExport = async (key: ManagementReportKey, format: 'xlsx' | 'pdf', filters: ManagementFilters): Promise<Blob> => {
  try {
    const response = await apiClient.get(`/management-reports/${key}/export`, {
      params: { ...params(filters), format },
      responseType: 'blob',
      timeout: 180_000,
    });
    return response.data as Blob;
  } catch (err) {
    throw new Error(await extractBlobErrorMessage(err, 'The report could not be exported. Please try again.'));
  }
};

export const managementReportFilename = (key: ManagementReportKey, format: 'xlsx' | 'pdf') =>
  `imc-${key}-${new Date().toISOString().slice(0, 10)}.${format}`;

export const fetchManagementReportPdf = (key: ManagementReportKey, filters: ManagementFilters) => fetchExport(key, 'pdf', filters);

export const downloadManagementReportExcel = async (key: ManagementReportKey, filters: ManagementFilters) => {
  downloadBlob(await fetchExport(key, 'xlsx', filters), managementReportFilename(key, 'xlsx'));
};

// Programme targets (management_reports:manage to change)

export interface ProgrammeTarget {
  id: number;
  universityId: number;
  universityName: string;
  countryName: string | null;
  courseId: number | null;
  courseName: string | null;
  intakeId: number;
  intakeName: string;
  intakeStart: string;
  target: number;
  notes: string | null;
}
export interface ProgrammeTargetInput {
  universityId: number;
  courseId: number | null;
  intakeId: number;
  target: number;
  notes: string | null;
}

export const listProgrammeTargets = async (): Promise<ProgrammeTarget[]> => {
  const { data } = await apiClient.get('/management-reports/programme-targets');
  return data.data;
};
export const createProgrammeTarget = async (input: ProgrammeTargetInput): Promise<ProgrammeTarget> => {
  const { data } = await apiClient.post('/management-reports/programme-targets', input);
  return data.data;
};
export const updateProgrammeTarget = async (id: number, input: ProgrammeTargetInput): Promise<ProgrammeTarget> => {
  const { data } = await apiClient.put(`/management-reports/programme-targets/${id}`, input);
  return data.data;
};
export const deleteProgrammeTarget = async (id: number): Promise<void> => {
  await apiClient.delete(`/management-reports/programme-targets/${id}`);
};

// Intake clean-up (Super Admin)

export interface IntakeMappingPreview {
  totalStudents: number;
  backupFresh: boolean;
  backupMaxAgeHours: number;
  latestBackup: { id: number; filename: string; completedAt: string | null } | null;
  labels: Array<{
    label: string;
    variants: string[];
    studentCount: number;
    suggestion: { month: number; year: number; intakeId: number | null; intakeName: string; willCreateIntake: boolean } | null;
    students: Array<{ id: number; studentNumber: string; name: string; isDraft: boolean; label: string; university: string | null; programme: string | null; feeScheduleWouldChange: boolean }>;
  }>;
}
export interface IntakeMappingResult {
  updated: number;
  createdIntakes: string[];
  skipped: Array<{ studentNumber: string; label: string; reason: string }>;
  backup: string;
}

export const previewIntakeMapping = async (): Promise<IntakeMappingPreview> => {
  const { data } = await apiClient.get('/management-reports/intake-mapping');
  return data.data;
};
export const applyIntakeMapping = async (input: { mappings: Array<{ label: string; month: number; year: number }>; expectedStudents: number }): Promise<IntakeMappingResult> => {
  const { data } = await apiClient.post('/management-reports/intake-mapping', input);
  return data.data;
};
